import $ from 'jquery';
import moment from 'moment';
import React from 'react';

import * as BatchCRISPRUtil from 'molbio/crispr/BatchCRISPRUtil';
import {
  ExportGuidesSequenceInfo,
  CRISPRStatsSequence,
  checkSequenceCRISPRStats,
  CheckCRISPRStatsResponse,
  exportGuides,
} from 'molbio/crispr/BatchGuideDesignDAO';
import notifications from 'notifications';
import {ListableItem} from 'search/SearchConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Modal from 'ui-library/modals/Modal';
import {listToReadable} from 'util/NameUtil';
import * as TaskRunner from 'util/TaskRunner';

const {parseCoordinates} = BatchCRISPRUtil;

// Targeted regions have up to three components, each with no spaces:
//   1. Sequence name
//   2. Exon names or CDS coords
//     - Exon names are words, cds coords are <start>-<end>, both are separated by commas
//   3. CDS percentage
//     - Must be number, either float or integer
//   3 is optional
// Valid Examples
//   sequence_name
//   sequence_name 10-100,200-300
//   sequence_name exon1,exon2 10.2
const TARGETED_REGION_REGEX = new RegExp(
  '^(\\S+)' + // Sequence name at start
    '\\s+' + // Must have spaces between groups
    '(?:' + // Non-capturing group to match cds coords or exon names
    '((?:\\d+-\\d+,?)+)' + // Match one or more cds coords separated by commas
    '|' +
    '((?:[\\w:+-],?)+)' + // Match one or more exon names separated by commas
    ')' +
    '(?:' +
    '\\s+' + // Must have spaces between groups
    '(\\d+\\.?\\d*)' + // Floating point number
    ')?$' // This last group is optional
);
const EXON_NAME_REGEX = /[\w:-]+/g;

// Exported for testing
export function parseExonNames(exonNamesString?: string): Array<string> {
  if (exonNamesString == null) {
    return [];
  }
  const exonNames = [];
  let match = EXON_NAME_REGEX.exec(exonNamesString);
  while (match != null) {
    exonNames.push(match[0]);
    match = EXON_NAME_REGEX.exec(exonNamesString);
  }
  return exonNames;
}

// Exported for testing
export function parseTargetedRegions(targetedRegionsText: string): Array<ExportGuidesSequenceInfo> | null {
  const lines = targetedRegionsText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length !== 0);

  const isTargetedRegions = lines.every((line) => TARGETED_REGION_REGEX.test(line));
  if (!isTargetedRegions) {
    notifications.warn(`Targeted regions must be formatted in the form: 1. sequence name, 2. exon names \
or CDS coordinates, and 3. CDS percentage (optional).`);
    return null;
  }

  return lines.map((line) => {
    const match = TARGETED_REGION_REGEX.exec(line);
    const [sequenceName, coordString, exonNamesString, cdsPercentageString] = match!.slice(1, 5);
    const cdsCoords = parseCoordinates(coordString);
    const exonNames = parseExonNames(exonNamesString);
    const cdsPercentage = cdsPercentageString != null ? parseFloat(cdsPercentageString) : null;
    return {sequenceName, cdsCoords, exonNames, cdsPercentage};
  });
}

export interface BatchExportGuidesModalProps {
  onRequestClose: () => void;
  sequences: Array<ListableItem>;
}

interface BatchExportGuidesModalState {
  numGuidesPerExon: number;
  minOffTargetScore: number;
  minOnTargetScore: number;
  cdsPercentage: number;
  batchExportGuidesLoading: boolean;
  targetedRegionsText: string;
}

class BatchExportGuidesModal extends React.Component<
  BatchExportGuidesModalProps,
  BatchExportGuidesModalState
> {
  state: BatchExportGuidesModalState = {
    numGuidesPerExon: 5,
    minOffTargetScore: 10,
    minOnTargetScore: 10,
    cdsPercentage: 100,
    batchExportGuidesLoading: false,
    targetedRegionsText: '',
  };

  checkSequencesForExport = (
    sequences: Array<CRISPRStatsSequence>,
    sequenceInfos: Array<ExportGuidesSequenceInfo>
  ): string | null => {
    let msg = null;
    const incorrectSequences = sequences.filter(({numCrisprAnalyses}) => numCrisprAnalyses !== 1);
    if (incorrectSequences.length) {
      msg = 'Exporting guides requires that each sequence have 1 CRISPR analysis.';
      const sequencesWithExtraAnalyses = incorrectSequences.filter(
        ({numCrisprAnalyses}) => numCrisprAnalyses > 1
      );
      if (sequencesWithExtraAnalyses.length) {
        msg += `\n\nSequences with more than 1 CRISPR analysis: \
${listToReadable(sequencesWithExtraAnalyses.map(({name}) => name))}`;
      }
      const sequencesWithNoAnalyses = incorrectSequences.filter(
        ({numCrisprAnalyses}) => numCrisprAnalyses === 0
      );
      if (sequencesWithNoAnalyses.length) {
        msg += `\n\nSequences with no CRISPR analysis: \
${listToReadable(sequencesWithNoAnalyses.map(({name}) => name))}`;
      }
      return msg;
    }

    const sequencesWithMultipleGroups = sequences.filter((sequence) => {
      const {translations} = sequence;
      const translationGroups = [...new Set(translations.map(({translationGroupId}) => translationGroupId))];
      return translationGroups.length > 1;
    });

    if (sequencesWithMultipleGroups.length > 0) {
      msg = `Exporting guides requires that each sequence have at most 1 translation group.\n\n \
Sequences with more than 1 translation group: \
${listToReadable(sequencesWithMultipleGroups.map(({name}) => name))}`;
      return msg;
    }

    const sequencesWithNoTranslationGroups = sequences.filter((sequence) => {
      const {translations} = sequence;
      return translations.some(({translationGroupId}) => translationGroupId == null);
    });

    if (sequencesWithNoTranslationGroups.length > 0) {
      msg = `Exporting guides require that all translations must be part of a translation group. \
\n\n Sequences with translations without a translation group: \
${listToReadable(sequencesWithNoTranslationGroups.map(({name}) => name))}`;
      return msg;
    }

    // Check that for every sequence info we have exactly one sequence with that name
    const nonMatchingSequenceInfos = sequenceInfos.filter(({sequenceName}) => {
      const matchingSequences = sequences.filter(({name}) => name === sequenceName);
      return matchingSequences.length === 0;
    });
    const ambiguousSequenceInfos = sequenceInfos.filter(({sequenceName}) => {
      const matchingSequences = sequences.filter(({name}) => name === sequenceName);
      return matchingSequences.length > 1;
    });

    if (nonMatchingSequenceInfos.length > 0 || ambiguousSequenceInfos.length > 0) {
      msg = 'Targeted regions sequences must match sequences being exported.';
      if (nonMatchingSequenceInfos.length > 0) {
        msg += `\n\n No matching sequences found for: \
${listToReadable(nonMatchingSequenceInfos.map(({sequenceName}) => sequenceName))}`;
      }
      if (ambiguousSequenceInfos.length > 0) {
        msg += `\n\n Multiple matching sequences found for: \
${listToReadable(ambiguousSequenceInfos.map(({sequenceName}) => sequenceName))}`;
      }
      return msg;
    }

    sequenceInfos.filter(({cdsCoords, exonNames}) => cdsCoords == null && exonNames == null);
    if (sequencesWithMultipleGroups.length > 0) {
      msg = 'Exon names or CDS coords must be specified for sequences ';
    }

    return null;
  };

  submitExportGuides = (): void => {
    const sequenceIds = this.props.sequences.map(({id}) => id);
    const sequenceInfos = parseTargetedRegions(this.state.targetedRegionsText);
    if (sequenceInfos == null) {
      return;
    }

    this.setState({batchExportGuidesLoading: true});
    checkSequenceCRISPRStats({sequenceIds})
      .then(({sequences}: CheckCRISPRStatsResponse) => {
        const sequencesError = this.checkSequencesForExport(sequences, sequenceInfos);
        if (sequencesError != null) {
          return $.Deferred().reject({userMessage: sequencesError}).promise();
        }
        return exportGuides({
          sequenceIds,
          numGuidesPerExon: this.state.numGuidesPerExon,
          minOffTargetScore: this.state.minOffTargetScore,
          minOnTargetScore: this.state.minOnTargetScore,
          cdsPercentage: this.state.cdsPercentage,
          startTime: moment().format('YYYY-MM-DD HH:mm'),
          sequenceInfos,
        }).then((response: TaskRunner.OngoingTaskResponse) => {
          TaskRunner.resumeTask(response.status_url).fail(notifications.notifyError);
        });
      })
      .always(() => {
        this.setState({batchExportGuidesLoading: false});
      })
      .fail((error) => {
        const {userMessage} = error;
        if (userMessage != null) {
          notifications.warn(userMessage);
        } else {
          notifications.notifyError(error);
        }
      })
      .done(() => {
        this.props.onRequestClose();
        notifications.success(`Exporting your guides. You will receive an email with the guides attached when \
export is complete.`);
      });
  };

  render(): JSX.Element {
    const buttons = [
      {
        text: this.state.batchExportGuidesLoading ? 'Exporting...' : 'Export guides',
        submit: true,
        disabled: this.state.batchExportGuidesLoading,
      },
    ];
    return (
      <Modal
        title="Batch export CRISPR guides"
        onRequestSubmit={this.submitExportGuides}
        onRequestClose={this.props.onRequestClose}
        buttons={buttons}
      >
        <FormGroup>
          <Row>
            <Col sm={10} className="col-sm-offset-1 u-small-spacing-bottom">
              <Label className="u-input-line-height">Targeted regions</Label>
              <textarea
                placeholder="Bak1                         exon1,exon2,exon4
        NW_003614972.1:224325-227949 225621-225756,226024-226167"
                value={this.state.targetedRegionsText}
                onChange={(e) => {
                  this.setState({targetedRegionsText: e.target.value});
                }}
                rows={5}
                className="mono"
              />
            </Col>
          </Row>
          <Row>
            <Col className="text-right col-sm-offset-1" sm={5}>
              <Label className="u-input-line-height">Max CDS %</Label>
            </Col>
            <Col sm={5}>
              <input
                type="number"
                required={true}
                min={0}
                max={100}
                step="any"
                value={this.state.cdsPercentage}
                onChange={(e) => {
                  this.setState({cdsPercentage: parseFloat(e.target.value)});
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col className="text-right col-sm-offset-1" sm={5}>
              <Label className="u-input-line-height">Min off-target score</Label>
            </Col>
            <Col sm={5}>
              <input
                type="number"
                required={true}
                min={0}
                max={100}
                step="any"
                value={this.state.minOffTargetScore}
                onChange={(e) => {
                  this.setState({minOffTargetScore: parseFloat(e.target.value)});
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col className="text-right col-sm-offset-1" sm={5}>
              <Label className="u-input-line-height">Min on-target score</Label>
            </Col>
            <Col sm={5}>
              <input
                type="number"
                required={true}
                min={0}
                max={100}
                step="any"
                value={this.state.minOnTargetScore}
                onChange={(e) => {
                  this.setState({minOnTargetScore: parseFloat(e.target.value)});
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col className="text-right col-sm-offset-1" sm={5}>
              <Label className="u-input-line-height">Number of guides per exon</Label>
            </Col>
            <Col sm={5}>
              <input
                type="number"
                required={true}
                min={0}
                value={this.state.numGuidesPerExon}
                onChange={(e) => {
                  this.setState({numGuidesPerExon: parseFloat(e.target.value)});
                }}
              />
            </Col>
          </Row>
        </FormGroup>
      </Modal>
    );
  }
}

export default BatchExportGuidesModal;
